import { useEffect, useState } from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Hidden,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  IdsExpandMore as IdsExpandMoreIcon,
  IdsExpandLess as IdsExpandLessIcon,
} from "components/shared/svgIcons";
import { makeStyles } from "@mui/styles";

import MarketingMessage from "components/catalog/MarketingMessage";
import { FusionPartnerBox } from "components/FusionPartnerBox";
import { KeyMetrics } from "components/KeyMetrics";
import { PanelHeaderBreadcrumb } from "components/PanelHeaderBreadcrumb";
import DesignAlerts from "components/shared/DesignAlerts";
import RightContent from "components/wrappers/RightContent";
import useApp from "hooks/useApp";
import useAuth from "hooks/useAuth";
import useCatalogDetail from "hooks/useCatalogDetail";
import { IDesign, Workflow } from "types/design";

import ContentfulDetails from "./components/ContentfulDetails";
import FindModule from "./components/FindModule";
import { FusionPartnerDialog } from "./components/FusionPartnerDialog";
import Header from "./components/Header";
import Table from "./components/Table";
import Files from "./components/Files";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(2, 0, 5),
      marginLeft: theme.spacing(-1),
      marginRight: theme.spacing(-1),
    },
  },
  accordion: {
    "&.MuiAccordion-root, &.MuiAccordion-root.Mui-expanded": {
      [theme.breakpoints.up("lg")]: { marginBottom: theme.spacing(5) },
      [theme.breakpoints.only("md")]: { marginBottom: theme.spacing(4) },
      [theme.breakpoints.down("md")]: { marginBottom: theme.spacing(3) },
    },
  },
  accordionAnon: {
    "&.MuiAccordion-root, &.MuiAccordion-root.Mui-expanded": {
      [theme.breakpoints.up("lg")]: { marginTop: theme.spacing(5) },
      [theme.breakpoints.only("md")]: { marginTop: theme.spacing(4) },
      [theme.breakpoints.down("md")]: { marginTop: theme.spacing(3) },
    },
  },
  accordionBtn: {
    [theme.breakpoints.down("sm")]: {
      width: "40px !important",
      minWidth: 40,
      paddingLeft: 0,
      paddingRight: 0,
      "& .MuiButton-endIcon": { margin: "0 !important" },
    },
  },
  accordionSummary: {
    minHeight: "0 !important",
    padding: 16,
    height: 64,
    boxSizing: "border-box",
    "& > .MuiAccordionSummary-content": {
      margin: 0,
    },
  },
  accordionDetails: {
    padding: 0,
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    display: "flex",
    maxHeight: (props: any) => (props.customize ? 390 : "inherit"),
  },
  pdpName: {
    [theme.breakpoints.up("sm")]: { marginBottom: theme.spacing(3) },
    [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(2) },
  },
}));

export const PanelDetailContent = () => {
  let {
    catalog,
    customize,
    diseaseType,
    isCustomPanel,
    isDraftCustomPanel,
    isSuggestedPanel,
    workflow,
    setWorkflow,
    setDiseaseType,
  } = useCatalogDetail();
  catalog = catalog as IDesign;
  const { isLoggedIn } = useAuth();
  const [expanded, setExpanded] = useState<boolean>(!customize);
  const classes = useStyles({ customize, isLoggedIn });
  const { pathname } = useLocation();
  const [parent, setParent] = useState<{ url: string; label: string }>();
  const [openDialog, setOpenDialog] = useState(false);
  const { getDiseaseTypes, getWorkflow } = useApp();

  const isLgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const isMdOnly = useMediaQuery((theme: Theme) =>
    theme.breakpoints.only("md")
  );
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  useEffect(() => {
    setExpanded(!customize);
  }, [customize, isLoggedIn]);

  useEffect(() => {
    if (isDraftCustomPanel) {
      setWorkflow(getWorkflow(catalog?.workflow));
      setParent({
        url: "/dashboard/draft-custom-panels",
        label: "Draft Panels",
      });
    } else if (pathname.indexOf("/catalog") === 0) {
      setWorkflow(getWorkflow(catalog?.workflow));
      setDiseaseType((getDiseaseTypes(catalog?.diseaseType) || [])[0]);
      setParent({
        url: "/catalog",
        label: "Catalog",
      });
    } else {
      setParent({
        url: `/evaluate-panel-options?workflow=${workflow?.id}&diseaseType=${diseaseType?.id}`,
        label: "List Of Panels",
      });
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [pathname, isDraftCustomPanel]);

  return (
    <RightContent className={classes.container}>
      <Box width={1}>
        <Box mb={1}>
          <PanelHeaderBreadcrumb parent={parent || {}} />
        </Box>

        <Box
          component="section"
          display="flex"
          flexDirection="column"
          gap={isLgUp ? "36px" : isMdOnly ? "28px" : "20px"}
        >
          <Header />
          <KeyMetrics />

          {!!customize && workflow?.name === Workflow.FUSION && (
            <FusionPartnerBox
              hideDismiss
              footer={() => (
                <Button
                  type="button"
                  color="secondary"
                  variant="outlined"
                  size="small"
                  onClick={() => setOpenDialog(true)}
                >
                  Known Fusion Partners
                </Button>
              )}
            />
          )}
          {!!customize && <FindModule workflow={workflow?.name as Workflow} />}
          {(!isCustomPanel || !customize) && (
            <Box>
              <Typography
                component="h2"
                variant={isMobile ? "h4" : "h2"}
                className={classes.pdpName}
              >
                {catalog.name}
              </Typography>
              {!customize && <ContentfulDetails />}
              {!isLoggedIn && <MarketingMessage />}
              <Accordion
                square
                expanded={expanded}
                onChange={() => {}}
                className={clsx(
                  classes.accordion,
                  !isLoggedIn && classes.accordionAnon
                )}
              >
                <AccordionSummary
                  className={classes.accordionSummary}
                  id={"target-list-header"}
                  aria-controls={"target-list-content"}
                >
                  <Box display="flex" justifyContent="space-between" width={1}>
                    <Typography variant="h4" component="h3">
                      Target List
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      color="secondary"
                      onClick={() => setExpanded((expanded) => !expanded)}
                      className={classes.accordionBtn}
                      endIcon={
                        expanded ? <IdsExpandLessIcon /> : <IdsExpandMoreIcon />
                      }
                    >
                      <Hidden only="xs">
                        {expanded ? "Collapse" : "Expand"}
                      </Hidden>
                    </Button>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Table workflow={workflow?.name as Workflow} />
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Box>
        {!customize && !isSuggestedPanel && (
          <Box component="section" marginBottom={isLoggedIn ? 5 : 0}>
            <Files />
          </Box>
        )}

        {isLoggedIn && <DesignAlerts />}
      </Box>

      {!!customize && (
        <FusionPartnerDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          row={catalog}
        />
      )}
    </RightContent>
  );
};
